import _ from 'lodash';
import server from '../utils/server';
import { createSelector } from '@reduxjs/toolkit';

export const getRecommendedEmployeeForReviewSelected = state => {
  const { reviewRecommendationState, employeeState } = state;
  const { selectedEmployeeId } = reviewRecommendationState;
  return employeeState.employeesById[selectedEmployeeId] || null;
};

export const reviewRecommendationState = (
  state = {
    employeeIdRecs: [],
    selectedEmployeeId: null,
    employeeRecFetchCount: 0,
    loadingEmployeeRecs: false,
    solicitationIdRecsByEmployeeId: {},
  },
  action
) => {
  switch (action.type) {
    case 'review_recommendations/BEGIN_LOAD_EMPLOYEES': {
      return {
        ...state,
        loadingEmployeeRecs: true,
      };
    }
    case 'review_recommendations/RECEIVED_EMPLOYEE_RECOMMENDATIONS': {
      const { employeeIds } = action;
      return {
        ...state,
        employeeRecFetchCount: state.employeeRecFetchCount + 1,
        employeeIdRecs: employeeIds,
        loadingEmployeeRecs: false,
      };
    }
    case 'review_recommendations/SET_SELECTED_EMPLOYEE': {
      const { employeeId } = action;
      return {
        ...state,
        selectedEmployeeId: employeeId,
      };
    }
    case 'review_recommendations/RECEIVED_SOLICITATION_RECOMMENDATIONS': {
      const { employeeId, solicitationIds } = action;
      return {
        ...state,
        solicitationIdRecsByEmployeeId: {
          ...state.solicitationIdRecsByEmployeeId,
          [employeeId]: solicitationIds,
        },
      };
    }
    default: {
      return state;
    }
  }
};

const updateRecommendedSolicitations = employeeId => async dispatch => {
  const { solicitations } = await server.get(`/api/v1/review_recommendations/${employeeId}/recent`);
  dispatch({
    type: 'solicitations/RECEIVED_STREAM_SOLICITATIONS',
    value: { solicitations },
  });
  dispatch({
    type: 'review_recommendations/RECEIVED_SOLICITATION_RECOMMENDATIONS',
    employeeId,
    solicitationIds: solicitations.map(solicitation => solicitation.id),
  });
};

export const updateRecommendedEmployees = () => async (dispatch, getState) => {
  dispatch({
    type: 'review_recommendations/BEGIN_LOAD_EMPLOYEES',
  });
  let newEmployees = await server.get('/api/v1/review_recommendations');
  let { selectedEmployeeId } = getState().reviewRecommendationState;
  // this is a bit of a hack to avoid writing a complex query on the back-end
  if (selectedEmployeeId && !newEmployees.some(e => e.id === selectedEmployeeId)) {
    const [selectedEmployee] = await server.get('/api/v1/review_recommendations', {
      employeeIds: [selectedEmployeeId],
    });
    if (selectedEmployee) {
      newEmployees = [selectedEmployee, ...newEmployees];
    } else {
      const employeeWithoutLatestFilterChange = getRecommendedEmployeeForReviewSelected(getState());
      newEmployees = [
        {
          ...employeeWithoutLatestFilterChange,
          solicitations: [],
        },
        ...newEmployees,
      ];
    }
  }
  const employeeIds = newEmployees.map(e => e.id);
  const { employeeRecFetchCount } = getState().reviewRecommendationState;
  dispatch({
    type: 'RECEIVED_EMPLOYEES',
    employees: newEmployees,
  });
  if (employeeRecFetchCount === 0) {
    dispatch({
      type: 'review_recommendations/RECEIVED_EMPLOYEE_RECOMMENDATIONS',
      employeeIds: _.shuffle(employeeIds),
    });
  }
  dispatch({
    type: 'review_recommendations/RECEIVED_EMPLOYEE_RECOMMENDATIONS',
    employeeIds,
  });
  // need to pull from getState() again because it may have updated
  selectedEmployeeId = getState().reviewRecommendationState.selectedEmployeeId;
  if (selectedEmployeeId) {
    dispatch(updateRecommendedSolicitations(selectedEmployeeId));
  }
};

export const setSelectedRevieweeSuggestion = employeeId => (dispatch, getState) => {
  const currentEmployeeSelected = getRecommendedEmployeeForReviewSelected(getState());
  if (currentEmployeeSelected && !_.get(currentEmployeeSelected, 'solicitations', []).length) {
    /*
    we're deselecting an reviewee suggestion that has no solicitations,
    so we should remove it from the list of suggested reviewees
    (It was probably still hanging around because it was selected when a filter
      change resulted in it having 0 solicitations to suggest)
      */
    const { employeeIdRecs } = getState().reviewRecommendationState;
    dispatch({
      type: 'review_recommendations/RECEIVED_EMPLOYEE_RECOMMENDATIONS',
      employeeIds: employeeIdRecs.filter(id => id !== currentEmployeeSelected.id),
    });
  }
  dispatch({
    type: 'review_recommendations/SET_SELECTED_EMPLOYEE',
    employeeId,
  });
  if (employeeId) {
    dispatch(updateRecommendedSolicitations(employeeId));
  }
};

const selectEmployeeIdRecs = state => state.reviewRecommendationState.employeeIdRecs;
const selectEmployeesById = state => state.employeeState.employeesById;

export const getEmployeesRecommendedForReview = createSelector(
  [selectEmployeeIdRecs, selectEmployeesById],
  (employeeIdRecs, employeesById) => {
    return employeeIdRecs.map(id => employeesById[id]);
  }
);

const selectSolicitationIdRecs = state =>
  state.reviewRecommendationState.solicitationIdRecsByEmployeeId;
const selectSolicitationsById = state => state.solicitationsState.reviewRecSolicitationsById;
const selectEmployeeId = (state, { employeeId }) => employeeId;

export const getSolicitationsRecommendedForReview = createSelector(
  [selectSolicitationIdRecs, selectSolicitationsById, selectEmployeeId],
  (idRecsByEmployee, solicitationsById, employeeId) => {
    const solicitationIds = idRecsByEmployee[employeeId] || [];

    return solicitationIds.map(sid => solicitationsById[sid]);
  }
);
